import { METADATA_TYPE, type MetadataType } from "@/constants/metadata-types"

import type {
  WorkspaceSettings,
  ConditionalMonitoringRuleResource as WorkspaceSettingsRule,
} from "@/types_api/backend/transport/workspace_settings"
import type {
  ConversationMetricRule,
  ConversationMetricRuleCondition,
} from "@/types/api/ConversationMetricRule"

type ConversationMetricRuleSource =
  | ConversationMetricRule
  | WorkspaceSettingsRule

type ConversationMetricRuleType = "RUN_METADATA"

type ConversationMetricRuleConditionDraft = ConversationMetricRuleCondition

interface ConversationMetricRuleDraft {
  type: ConversationMetricRuleType
  metadataConditions: ConversationMetricRuleConditionDraft[]
  metrics: string[]
}

type ConversationMetricRuleDraftOverrides = Partial<
  Omit<ConversationMetricRuleDraft, "metadataConditions">
> &
  Partial<ConversationMetricRuleConditionDraft> & {
    metadataConditions?: ConversationMetricRuleConditionDraft[]
  }

interface ConversationMetricsConfigurationSnapshot {
  savedMetrics: string[]
  savedRules: ConversationMetricRule[]
  tempSelectedMetrics: string[]
  tempRules: ConversationMetricRule[]
}

const DEFAULT_CONVERSATION_METRIC_RULE_DRAFT: ConversationMetricRuleDraft = {
  type: "RUN_METADATA",
  metadataConditions: [
    {
      metadataKey: "",
      metadataType: METADATA_TYPE.STRING,
      matchType: "EQUALS",
      metadataValue: "",
    },
  ],
  metrics: [],
}

export const createConversationMetricRuleConditionDraft = (
  overrides: Partial<ConversationMetricRuleConditionDraft> = {},
): ConversationMetricRuleConditionDraft => ({
  metadataKey: "",
  metadataType: METADATA_TYPE.STRING,
  matchType: "EQUALS",
  metadataValue: "",
  ...overrides,
})

export const createConversationMetricRuleDraft = (
  overrides: ConversationMetricRuleDraftOverrides = {},
): ConversationMetricRuleDraft => {
  const conditionOverrides: Partial<ConversationMetricRuleConditionDraft> = {}
  if (overrides.metadataKey !== undefined) {
    conditionOverrides.metadataKey = overrides.metadataKey
  }
  if (overrides.metadataType !== undefined) {
    conditionOverrides.metadataType = overrides.metadataType
  }
  if (overrides.matchType !== undefined) {
    conditionOverrides.matchType = overrides.matchType
  }
  if (overrides.metadataValue !== undefined) {
    conditionOverrides.metadataValue = overrides.metadataValue
  }

  return {
    type: overrides.type ?? DEFAULT_CONVERSATION_METRIC_RULE_DRAFT.type,
    metadataConditions: overrides.metadataConditions
      ? overrides.metadataConditions.map((condition) => ({ ...condition }))
      : [createConversationMetricRuleConditionDraft(conditionOverrides)],
    metrics: overrides.metrics ? [...overrides.metrics] : [],
  }
}

export const getRuleMetrics = (
  rule: ConversationMetricRuleSource,
): string[] => {
  if (rule.metrics && rule.metrics.length > 0) {
    return rule.metrics
  }

  if (rule.metric) {
    return [rule.metric]
  }

  return []
}

export const getRuleMetadataConditions = (
  rule: ConversationMetricRuleSource,
): ConversationMetricRuleCondition[] => {
  if (rule.metadataConditions && rule.metadataConditions.length > 0) {
    return rule.metadataConditions.map((condition) => ({ ...condition }))
  }

  return [
    {
      metadataKey: rule.metadataKey,
      metadataType: rule.metadataType || METADATA_TYPE.STRING,
      matchType: rule.matchType || "EQUALS",
      metadataValue: rule.metadataValue ?? "",
    },
  ]
}

export const validateAndMigrateRule = (
  rule: ConversationMetricRuleSource,
): ConversationMetricRule => {
  const metadataConditions = getRuleMetadataConditions(rule)
  const primaryCondition =
    metadataConditions[0] ?? createConversationMetricRuleConditionDraft()
  const validatedRule: ConversationMetricRule = {
    id: rule.id,
    type: rule.type || "RUN_METADATA",
    metadataKey: primaryCondition.metadataKey,
    metadataType: primaryCondition.metadataType || METADATA_TYPE.STRING,
    matchType: primaryCondition.matchType || "EQUALS",
    metadataValue: primaryCondition.metadataValue ?? "",
    metadataConditions:
      metadataConditions.length > 1 ? metadataConditions : undefined,
    metrics: getRuleMetrics(rule),
  }

  const { metric: _legacyMetric, ...normalizedRule } = validatedRule

  return normalizedRule
}

export const getConversationMetricsConfigurationSnapshot = (
  workspaceSettings?: Partial<WorkspaceSettings> | null,
): Pick<
  ConversationMetricsConfigurationSnapshot,
  "savedMetrics" | "savedRules"
> => ({
  savedMetrics: [...(workspaceSettings?.default_metrics || [])],
  savedRules: Array.isArray(workspaceSettings?.conditional_metrics)
    ? workspaceSettings.conditional_metrics.map(validateAndMigrateRule)
    : [],
})

export const hasConversationMetricsConfigurationChanges = ({
  savedMetrics,
  savedRules,
  tempSelectedMetrics,
  tempRules,
}: ConversationMetricsConfigurationSnapshot): boolean =>
  JSON.stringify([...tempSelectedMetrics].sort()) !==
    JSON.stringify([...savedMetrics].sort()) ||
  JSON.stringify(tempRules) !== JSON.stringify(savedRules)

export const createConversationMetricRuleDraftFromRule = (
  rule: ConversationMetricRule,
): ConversationMetricRuleDraft =>
  createConversationMetricRuleDraft({
    type: rule.type || "RUN_METADATA",
    metadataConditions: getRuleMetadataConditions(rule),
    metrics: getRuleMetrics(rule),
  })

const applyConversationMetricRuleConditionMetadataType = (
  condition: ConversationMetricRuleConditionDraft,
  metadataType: MetadataType,
): ConversationMetricRuleConditionDraft => {
  switch (metadataType) {
    case METADATA_TYPE.BOOLEAN:
      return {
        ...condition,
        metadataType,
        matchType: "IS_TRUE",
        metadataValue: true,
      }
    case METADATA_TYPE.STRING_ARRAY:
      return {
        ...condition,
        metadataType,
        matchType: "CONTAINS_ANY",
        metadataValue: [],
      }
    case METADATA_TYPE.STRING:
    default:
      return {
        ...condition,
        metadataType: METADATA_TYPE.STRING,
        matchType: "EQUALS",
        metadataValue: "",
      }
  }
}

const LENGTH_AT_LEAST_DEFAULT = 1

/**
 * LENGTH_AT_LEAST carries a numeric threshold while every other array operator carries a list
 * of values, so switching between them has to reshape the value or the rule saves as invalid.
 */
const applyConversationMetricRuleConditionMatchType = (
  condition: ConversationMetricRuleConditionDraft,
  matchType: ConversationMetricRule["matchType"],
): ConversationMetricRuleConditionDraft => {
  if (matchType === condition.matchType) {
    return { ...condition, matchType }
  }
  if (matchType === "LENGTH_AT_LEAST") {
    return { ...condition, matchType, metadataValue: LENGTH_AT_LEAST_DEFAULT }
  }
  if (condition.matchType === "LENGTH_AT_LEAST") {
    return { ...condition, matchType, metadataValue: [] }
  }
  return { ...condition, matchType }
}

export const applyConversationMetricRuleMatchType = (
  draft: ConversationMetricRuleDraft,
  matchType: ConversationMetricRule["matchType"],
  conditionIndex = 0,
): ConversationMetricRuleDraft => ({
  ...draft,
  metadataConditions: draft.metadataConditions.map((condition, index) =>
    index === conditionIndex
      ? applyConversationMetricRuleConditionMatchType(condition, matchType)
      : condition,
  ),
})

export const applyConversationMetricRuleMetadataType = (
  draft: ConversationMetricRuleDraft,
  metadataType: MetadataType,
  conditionIndex = 0,
): ConversationMetricRuleDraft => ({
  ...draft,
  metadataConditions: draft.metadataConditions.map((condition, index) =>
    index === conditionIndex
      ? applyConversationMetricRuleConditionMetadataType(
          condition,
          metadataType,
        )
      : condition,
  ),
})

export const createConversationMetricRuleFromDraft = ({
  draft,
  id,
}: {
  draft: ConversationMetricRuleDraft
  id: string
}): ConversationMetricRule => {
  const metadataConditions = draft.metadataConditions.map((condition) => ({
    ...condition,
  }))
  const primaryCondition =
    metadataConditions[0] ?? createConversationMetricRuleConditionDraft()

  return {
    id,
    type: "RUN_METADATA",
    metrics: draft.metrics,
    metadataKey: primaryCondition.metadataKey,
    metadataType: primaryCondition.metadataType,
    matchType: primaryCondition.matchType,
    metadataValue: primaryCondition.metadataValue,
    metadataConditions:
      metadataConditions.length > 1 ? metadataConditions : undefined,
  }
}

export const replaceConversationMetricRule = (
  rules: ConversationMetricRule[],
  updatedRule: ConversationMetricRule,
): ConversationMetricRule[] =>
  rules.map((rule) => (rule.id === updatedRule.id ? updatedRule : rule))

export const removeConversationMetricRule = (
  rules: ConversationMetricRule[],
  ruleId: string,
): ConversationMetricRule[] => rules.filter((rule) => rule.id !== ruleId)

export const copyConversationMetricRule = ({
  id,
  rule,
}: {
  id: string
  rule: ConversationMetricRule
}): ConversationMetricRule => ({
  ...rule,
  id,
})

export const canSubmitConversationMetricRule = (
  draft: ConversationMetricRuleDraft,
  existingRule?: ConversationMetricRule,
): boolean => {
  if (
    !draft.type ||
    draft.metrics.length === 0 ||
    draft.metadataConditions.length === 0
  ) {
    return false
  }

  const existingConditions = existingRule
    ? getRuleMetadataConditions(existingRule)
    : []

  return draft.metadataConditions.every((condition, conditionIndex) => {
    if (!condition.metadataKey.trim()) {
      return false
    }

    if (condition.metadataType === METADATA_TYPE.STRING) {
      return Boolean(condition.metadataValue)
    }

    if (condition.metadataType === METADATA_TYPE.STRING_ARRAY) {
      if (condition.matchType === "LENGTH_AT_LEAST") {
        return (
          typeof condition.metadataValue === "number" &&
          Number.isInteger(condition.metadataValue) &&
          condition.metadataValue >= 0
        )
      }
      if (
        !Array.isArray(condition.metadataValue) ||
        condition.metadataValue.length === 0
      ) {
        return false
      }

      if (condition.metadataValue.every((value) => value.trim() !== "")) {
        return true
      }

      const existingCondition = existingConditions[conditionIndex]
      const existingValues = existingCondition?.metadataValue

      return (
        existingCondition?.metadataType === METADATA_TYPE.STRING_ARRAY &&
        existingCondition.matchType === condition.matchType &&
        Array.isArray(existingValues) &&
        condition.metadataValue.some((value) => value.trim() !== "") &&
        condition.metadataValue.length === existingValues.length &&
        condition.metadataValue.every(
          (value, valueIndex) => value === existingValues[valueIndex],
        )
      )
    }

    return true
  })
}
